import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { fetchRemoteBranchSalesByProduct, fetchWithRetry, isOnline } from '../../remote-api'
import type { SalesByProductSortKey } from '../../domain/reportsTypes'
import type { ReportsQueryScope } from '../../domain/reportsQueryScope';
import type { BranchSalesByProductRow } from '../../domain/reportsTypes';
import type { DateRangePreset } from '../reportsScreen.constants';
import { getReportsDateRange } from '../reportsScreen.utils';

const SALES_BY_PRODUCT_PAGE_SIZE = 10;

type Params = {
  currentBranchId: string | null | undefined;
  isVentasSection: boolean;
  isFocused: boolean;
  reportsBootstrapReady: boolean;
  dateRangePreset: DateRangePreset;
  customRangeFrom: Date;
  customRangeTo: Date;
  reportsQueryScope: ReportsQueryScope;
  salesByProductRefreshKey: number;
  bumpSalesByProductRefreshKey: () => void;
};

export function useReportsSalesByProduct({
  currentBranchId,
  isVentasSection,
  isFocused,
  reportsBootstrapReady,
  dateRangePreset,
  customRangeFrom,
  customRangeTo,
  reportsQueryScope,
  salesByProductRefreshKey,
  bumpSalesByProductRefreshKey,
}: Params) {
  const [salesByProductRows, setSalesByProductRows] = useState<BranchSalesByProductRow[]>([]);
  const [salesByProductTotalCount, setSalesByProductTotalCount] = useState(0);
  const [salesByProductSumQuantity, setSalesByProductSumQuantity] = useState(0);
  const [salesByProductSumLineTotal, setSalesByProductSumLineTotal] = useState(0);
  const [salesByProductUniformUnitOfMeasure, setSalesByProductUniformUnitOfMeasure] = useState(true);
  const [salesByProductSource, setSalesByProductSource] = useState<'server' | null>(null);
  const [salesByProductLoading, setSalesByProductLoading] = useState(false);
  const [salesByProductPage, setSalesByProductPage] = useState(0);
  const [salesByProductSort, setSalesByProductSort] = useState<SalesByProductSortKey>('quantity_desc');
  // Stack of cumulative lineTotal at each page boundary: abcStack[i] = sum of lineTotal for pages 0..i-1
  const abcStackRef = useRef<number[]>([0]);
  const currentPageLineTotalRef = useRef(0);

  // Track sum of lineTotal of current page rows
  useEffect(() => {
    currentPageLineTotalRef.current = salesByProductRows.reduce((sum, r) => sum + r.lineTotal, 0);
  }, [salesByProductRows]);

  const abcCumulativeBefore = abcStackRef.current[salesByProductPage] ?? 0;

  const goToNextSalesByProductPage = useCallback(() => {
    setSalesByProductPage((p) => {
      const nextPage = p + 1;
      // Push cumulative for the next page = current cumulative + this page's total
      const currentCumulative = abcStackRef.current[p] ?? 0;
      abcStackRef.current[nextPage] = currentCumulative + currentPageLineTotalRef.current;
      return nextPage;
    });
  }, []);

  const goToPrevSalesByProductPage = useCallback(() => {
    setSalesByProductPage((p) => Math.max(0, p - 1));
  }, []);

  useLayoutEffect(() => {
    setSalesByProductPage(0);
    abcStackRef.current = [0];
  }, [
    currentBranchId,
    dateRangePreset,
    customRangeFrom,
    customRangeTo,
    salesByProductSort,
    reportsQueryScope,
  ]);

  useEffect(() => {
    if (!currentBranchId || !isVentasSection || !isFocused || !reportsBootstrapReady) {
      setSalesByProductRows([]);
      setSalesByProductTotalCount(0);
      setSalesByProductSumQuantity(0);
      setSalesByProductSumLineTotal(0);
      setSalesByProductUniformUnitOfMeasure(true);
      setSalesByProductSource(null);
      setSalesByProductLoading(false);
      return;
    }

    let cancelled = false;
    setSalesByProductLoading(true);

    void (async () => {
      const online = await isOnline();
      if (!online) {
        if (!cancelled) {
          setSalesByProductRows([]);
          setSalesByProductTotalCount(0);
          setSalesByProductSumQuantity(0);
          setSalesByProductSumLineTotal(0);
          setSalesByProductUniformUnitOfMeasure(true);
          setSalesByProductSource(null);
          setSalesByProductLoading(false);
        }
        return;
      }

      const range = getReportsDateRange(
        new Date(),
        dateRangePreset,
        dateRangePreset === 'custom' ? { from: customRangeFrom, to: customRangeTo } : null
      );

      const res = await fetchWithRetry(() =>
        fetchRemoteBranchSalesByProduct({
          branchId: currentBranchId,
          fromIso: range.fromIso,
          toIso: range.toIso,
          queryScope: reportsQueryScope,
          sort: salesByProductSort,
          limit: SALES_BY_PRODUCT_PAGE_SIZE,
          offset: salesByProductPage * SALES_BY_PRODUCT_PAGE_SIZE,
        })
      );

      if (cancelled) return;

      if (res) {
        setSalesByProductRows(res.rows);
        setSalesByProductTotalCount(res.totalCount);
        setSalesByProductSumQuantity(res.sumQuantity);
        setSalesByProductSumLineTotal(res.sumLineTotal);
        setSalesByProductUniformUnitOfMeasure(res.uniformUnitOfMeasure);
        setSalesByProductSource('server');
      } else {
        setSalesByProductRows([]);
        setSalesByProductTotalCount(0);
        setSalesByProductSumQuantity(0);
        setSalesByProductSumLineTotal(0);
        setSalesByProductUniformUnitOfMeasure(true);
        setSalesByProductSource(null);
      }
      setSalesByProductLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    currentBranchId,
    isVentasSection,
    isFocused,
    reportsBootstrapReady,
    dateRangePreset,
    customRangeFrom,
    customRangeTo,
    salesByProductPage,
    salesByProductSort,
    salesByProductRefreshKey,
    reportsQueryScope,
  ]);

  return {
    salesByProductRows,
    salesByProductTotalCount,
    salesByProductSumQuantity,
    salesByProductSumLineTotal,
    salesByProductUniformUnitOfMeasure,
    salesByProductSource,
    salesByProductLoading,
    salesByProductPage,
    setSalesByProductPage,
    goToNextSalesByProductPage,
    goToPrevSalesByProductPage,
    salesByProductSort,
    setSalesByProductSort,
    salesByProductPageSize: SALES_BY_PRODUCT_PAGE_SIZE,
    salesByProductAbcCumulativeBefore: abcCumulativeBefore,
    bumpSalesByProductRefreshKey,
  };
}
